/**
 * Column configuration for the shared <app-data-table> component.
 */
export type ColumnType = 'text' | 'date' | 'pill' | 'template';

export type StatusVariant = 'amber' | 'green' | 'gray' | 'purple' | 'blue' | 'red';

export interface ColumnDef<T = any> {
  /** Property name on the row object (also the lookup key for template columns). */
  field: string;
  /** Header text; also used as the data-label when the row stacks on mobile. */
  header: string;
  /** Rendering strategy. Defaults to 'text'. */
  type?: ColumnType;
  /** Horizontal alignment of the cell content. Defaults to 'left'. */
  align?: 'left' | 'center' | 'right';
  /** Whether the column header toggles client-side sorting. */
  sortable?: boolean;
  /** Fixed column width, e.g. '15%' or '120px'. Emitted into <colgroup>. */
  width?: string;
  /** Date format for type 'date'. Defaults to 'dd/MM/yyyy'. */
  dateFormat?: string;
  /** Maps a row to a pill colour variant, for type 'pill'. */
  pillMap?: (row: T) => StatusVariant;
  /** Hide this column when the table stacks on mobile. */
  hideOnMobile?: boolean;
  /** Name of the projected <ng-template appColumnCell="..."> for type 'template'. */
  templateName?: string;
}

/** Emitted by row-level actions (currently the eye "view" button). */
export interface RowAction<T = any> {
  action: string;
  row: T;
}

/** Sort direction used by the shared table and emitted with SortChange. */
export type SortDirection = 'asc' | 'desc';

/** Emitted when the current page or page size changes. */
export interface PageChange {
  page: number;
  pageSize: number;
}

/** Emitted when the user sorts a column. */
export interface SortChange {
  field: string;
  direction: SortDirection;
}

/**
 * Page-size choices offered by every table, so the control is identical wherever it appears.
 * Capped at 100 — a larger page would undo the point of paging server-side.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS: number[] = [5, 10, 25, 50, 100];

/**
 * Page size used when a screen does not specify one. Matches the API's
 * `PagedRequest.DefaultPageSize` so the two ends do not disagree.
 */
export const DEFAULT_PAGE_SIZE: number = 5;
